import React from 'react';
import { motion } from 'motion/react';
import { Card } from './UI';

interface EmojiPickerProps {
  onSelect: (emoji: string) => void;
  currentEmoji?: string;
  onClose: () => void;
}

export function EmojiPicker({ onSelect, currentEmoji, onClose }: EmojiPickerProps) {
  const [value, setValue] = React.useState(currentEmoji || '');
  const inputRef = React.useRef<HTMLInputElement>(null);

  // `top-1/2` centres against the full layout viewport, which doesn't shrink
  // when the iOS keyboard opens — the popup ends up centred on ground the
  // keyboard now covers, mostly hidden behind it. Track visualViewport
  // (which DOES shrink to the actually-visible area) and centre against that
  // instead, so opening the keyboard (autoFocus below triggers it) re-centres
  // the popup into the remaining visible space above it.
  const [viewportCenter, setViewportCenter] = React.useState<number | null>(null);
  React.useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => setViewportCenter(vv.offsetTop + vv.height / 2);
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  // Keep a ref in sync with the latest emoji so handleSave always reads the
  // right value even when onBlur fires in the same tick as onChange — before
  // React has had a chance to commit the new state and run any useEffect.
  // We update it synchronously inside onChange rather than via useEffect so
  // there is zero window where the ref can be stale.
  const valueRef = React.useRef(currentEmoji || '');

  const handleSave = React.useCallback(() => {
    // Prefer the ref (always up-to-date) over the closure-captured state value
    const current = valueRef.current.trim();
    if (current) {
      onSelect(current);
    }
    onClose();
  }, [onSelect, onClose]);


  return (
    <>
      <div 
        className="fixed inset-0 bg-black/5 backdrop-blur-[1px] z-[90]" 
        onClick={handleSave} 
      />
      
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 10 }}
        style={viewportCenter !== null ? { top: viewportCenter } : undefined}
        className={`fixed left-1/2 -translate-x-1/2 -translate-y-1/2 z-[100] w-[82px] ${viewportCenter === null ? 'top-1/2' : ''}`}
      >
        <Card className="surface-solid bg-white border border-gold/40 shadow-2xl rounded-[28px] overflow-hidden p-3 flex flex-col items-center">
          <div className="w-full relative">
            <input
              ref={inputRef}
              name="emoji-input"
              autoComplete="off"
              autoFocus
              className="w-14 h-14 mx-auto text-center text-balance text-3xl bg-gold/5 rounded-2xl border-none outline-none focus:ring-0 placeholder:text-app-sub/20 p-0"
              placeholder="?"
              maxLength={10}
              value={value}
              onBlur={handleSave}
              onChange={e => {
                const val = e.target.value;
                // Use Segmenter to correctly handle multi-unicode emojis (flags, skin tones, etc)
                // Fallback to simple split if not available (modern browsers support it)
                const segments = (Intl as any).Segmenter ?
                  Array.from(new (Intl as any).Segmenter().segment(val)).map((s: any) => s.segment) :
                  Array.from(val);

                const lastChar = segments[segments.length - 1] || '';
                // Update ref synchronously — before React commits the state update —
                // so handleSave always sees the current emoji even on rapid type+blur.
                valueRef.current = lastChar;
                setValue(lastChar);
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') handleSave();
                if (e.key === 'Escape') onClose();
              }}
            />
          </div>
        </Card>
      </motion.div>
    </>
  );
}
